// SPDX-License-Identifier: Apache-2.0

import React, { useState } from "react";
import { Button, ButtonGroup } from "react-bootstrap";
import { ChainNames } from "../constants/Constants";
import { switchNetwork } from "../web3/web3";
import { ethers } from "ethers";
import {
  dispatchMetamaskError,
  dispatchPerunError,
  revertMetamaskError,
} from "../events/Error";
import { useTranslation } from "react-i18next";
import { Network } from "@ethersproject/networks/src.ts/types";
import connected from "../images/symbols/connected.png";
import notConnected from "../images/symbols/not_connected.png";
import { startNetworkChangeDetection } from "../networkDetection";

interface ChainsListProps {
  onProcessChange: (isProcessing: boolean) => void;
  onConnected: (isConnected: boolean) => void;
  externalProvider: ethers.providers.Web3Provider;
  onAccountChanged?: (addr: string | null) => void;
  networks: number[];
}

function ChainsList({
  onProcessChange,
  onConnected,
  externalProvider,
  onAccountChanged,
  networks,
}: ChainsListProps) {
  const { t } = useTranslation();
  const [isClickable, setIsClickable] = useState<boolean>(true);
  const [connChainID, setConnChainID] = useState<number>();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onNetworkChange = (newNetwork: Network) => {
    console.log("detected network change", newNetwork);
    if (window.localStorage.getItem("site") === "ONBOARDING") {
      console.log(`network changed to ${newNetwork.chainId}`, connChainID);
      setConnChainID(newNetwork.chainId);
      window.localStorage.setItem("chainID", String(newNetwork.chainId));
      if (networks.includes(newNetwork.chainId)) {
        onConnected(true);
      } else {
        console.log(
          "config includes ID: ",
          networks.includes(newNetwork.chainId),
          networks,
          newNetwork.chainId,
        );
        onConnected(false);
      }
      window.localStorage.setItem("chainID", newNetwork.chainId.toString());
    } else if (window.localStorage.getItem("site") === "SWAP") {
      console.log(`network changed to ${newNetwork.chainId}`);
      setConnChainID(newNetwork.chainId);
      onConnected(true);
      if (
        window.localStorage.getItem("chainID") !== newNetwork.chainId.toString()
      ) {
        dispatchMetamaskError(
          <p>
            {t("errorUnexpectedNetwork", {
              currID: newNetwork.chainId.toString(),
              chainID: window.localStorage.getItem("chainID"),
            })}
          </p>,
        );
      } else {
        revertMetamaskError();
      }
    } else if (window.localStorage.getItem("site") === "HISTORY") {
      console.log(`network changed to ${newNetwork.chainId}`);
      setConnChainID(newNetwork.chainId);
      onConnected(true);
    }
  };

  React.useEffect(() => {
    if (externalProvider) {
      console.log("starting network detection", externalProvider);
      startNetworkChangeDetection(externalProvider, onNetworkChange);
    }
  }, []);

  const connectToChain = async (netID: number) => {
    if (externalProvider._network.chainId === netID) {
      console.log("already connected to chain", netID);
      setConnChainID(netID);
      onConnected(true);
      return;
    }
    if (!isClickable || netID === connChainID) {
      console.log("not clickable or already connected");
      setIsClickable(true);
      setConnChainID(netID);
      onConnected(true);
      return;
    }
    console.log("connecting to chain", netID);
    setIsClickable(false);
    onConnected(false);
    if (!externalProvider) {
      console.log("no external provider");
      setIsClickable(true);
      onProcessChange(false);
      return;
    }
    onProcessChange(true);
    try {
      console.log("switching network", BigInt(netID));
      const switched = await switchNetwork(externalProvider, BigInt(netID));
      if (switched) {
        setConnChainID(netID);
        window.localStorage.setItem("chainID", String(netID));
        if (onAccountChanged) {
          externalProvider.listAccounts().then((accs) => {
            if (accs.length > 0) {
              onAccountChanged(ethers.utils.getAddress(accs[0]));
            } else {
              onAccountChanged(null);
            }
          });
        }
        onProcessChange(false);
        onConnected(true);
      } else {
        setConnChainID(undefined);
        onConnected(false);
      }
    } catch (error) {
      if (error instanceof Error) {
        dispatchPerunError(<p>{error.message}</p>);
      } else {
        dispatchPerunError(<p>{t("errorSwitchingNetwork")}</p>);
      }
    }
    setIsClickable(true);
  };

  const renderChainButton = (netID: number) => (
    <Button
      className={"chain-button"}
      key={netID}
      variant={"secondary"}
      disabled={!isClickable || netID === connChainID}
      onClick={() => connectToChain(netID)}
    >
      {netID === connChainID ? t("connectedto") : t("connectto")}
      {ChainNames.get(BigInt(netID))}{" "}
      {netID === connChainID ? (
        <img
          className={"conn-symbol"}
          src={connected}
          width={"20"}
          alt={"connected"}
        />
      ) : (
        <img
          className={"conn-symbol"}
          src={notConnected}
          width={"20"}
          alt={"not connected"}
        />
      )}
    </Button>
  );

  const isSingle = networks.length === 1;

  return (
    <div className={`chains-list${isSingle ? " chains-list-single" : ""}`}>
      {isSingle ? (
        renderChainButton(networks[0])
      ) : (
        <ButtonGroup vertical>{networks.map(renderChainButton)}</ButtonGroup>
      )}
    </div>
  );
}

export default ChainsList;
